<template>
  <div class="dtp-container">
    <WdsInput
      ref="date"
      :value="displayValue"
      :label="label"
      :placeholder="placeholder"
      :disabled="disabled"
      :name="name"
      :required="required"
      :warning="warning"
      :error="error"
      prefix-icon="calendar"
      type="text"
      autocomplete="off"
    />
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { WdsInput } from '@worksome/wds-components'

import { fZipArg } from '@/utils/fZip'
import Datepicker from '@/utils/forms/Datepicker'

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },

  name: {
    type: String,
    default: 'date',
  },

  format: {
    type: String,
    default: 'DD/MM/YYYY',
  },

  label: {
    type: String,
    default: null,
  },

  placeholder: {
    type: String,
    default: 'Select date',
  },

  disabled: {
    type: Boolean,
    default: false,
  },

  required: {
    type: Boolean,
    default: false,
  },

  minDate: {
    type: String,
    default: '',
  },

  maxDate: {
    type: String,
    default: '',
  },

  error: {
    type: String,
    default: null,
  },

  warning: {
    type: String,
    default: null,
  },
})

const emit = defineEmits(['update:modelValue', 'hide', 'show'])

const date = ref(null)
const picker = ref(null)
const show = ref(false)
const displayValHidden = ref(props.modelValue)

const dateLimits = computed(() => ({
  min: props.minDate,
  max: props.maxDate,
}))

const displayValue = computed({
  get() {
    return displayValHidden.value
  },

  set() {
    emit('update:modelValue', displayValue.value)
  },
})

const openDatetimePicker = () => {
  if (!show.value) {
    emit('show')
  }

  show.value = true
}

const closeDatetimePicker = () => {
  if (show.value) {
    emit('hide')
  }

  show.value = false
}

/**
 * This method will attach all the needed event listeners that are on the DatePicker.
 *
 * @see https://mymth.github.io/vanillajs-datepicker/#/api?id=events
 */
const attachListenerToCalendarChanges = () => {
  // Handle event from the underlying library
  const update = () => {
    emit(
      'update:modelValue',
      Datepicker.formatDate(picker.value.getDate(), props.format.toLowerCase()),
    )
  }

  const datePickerInputEl = date.value.$el.querySelector('input')

  datePickerInputEl.addEventListener('changeDate', fZipArg(update, closeDatetimePicker))
  datePickerInputEl.addEventListener('changeMonth', update)
  datePickerInputEl.addEventListener('changeYear', update)
  datePickerInputEl.addEventListener('show', () => openDatetimePicker)
  datePickerInputEl.addEventListener('hide', () => closeDatetimePicker)
}

/**
 * Simply shorthand to parse a date with the Datepicker library.
 */
const parse = (d) => Datepicker.parseDate(d, props.format.toLowerCase())

watch(
  () => dateLimits.value,
  ({ min, max }) => {
    nextTick(() => {
      picker.value?.setOptions({
        minDate: min || '',
        maxDate: max || '',
      })

      if (parse(min) > parse(displayValue.value)) {
        picker.value?.setDate(min)
      }

      if (parse(max) < parse(displayValue.value)) {
        picker.value?.setDate(max)
      }
    })
  },
  {
    deep: true,
    immediate: true,
  },
)

watch(
  () => props.modelValue,
  (v) => {
    if (v !== displayValue.value) {
      picker.value?.setDate(v)

      displayValHidden.value =
        Datepicker.formatDate(picker.value.getDate(), props.format.toLowerCase()) ??
        props.modelValue
    }
  },
)

onMounted(() => {
  const includesDays = props.format.toLowerCase().includes('dd')

  picker.value = new Datepicker(date.value.$el.querySelector('input'), {
    // We lowercase the format as its uppercase formatting is weird and the lowercase formatting works like moment
    format: props.format.toLowerCase(),
    language: currentUser?.locale ?? 'en',
    autohide: true,
    pickLevel: includesDays ? 0 : 1,
    orientation: 'bottom auto',
    maxView: 2,
    maxDate: props.maxDate,
    minDate: props.minDate,
    prevArrow: `<span
                    class="glyphicon glyphicon-arrow-left"
                    title="${$t('Previous Year')}">
                  </span>`,
    nextArrow: `<span
                    class="glyphicon glyphicon-arrow-right"
                    title="${$t('Next Year')}">
                  </span>`,
  })

  picker.value.setDate(props.modelValue)

  attachListenerToCalendarChanges()
})
</script>

<style lang="scss" scoped>
@import 'node_modules/vanillajs-datepicker/sass/mixins';
@import 'node_modules/vanillajs-datepicker/sass/datepicker';
@import 'resources/sass/frontend/_color-variables';

.datepicker-dropdown {
  z-index: 2000;
}

.datepicker-picker {
  font-size: 14px;
}

.datepicker-header {
  padding: 0.875rem 0;
  background-color: $gray-900;
  .datepicker-controls {
    .button,
    .button:hover {
      font-size: 15px;
      text-transform: uppercase;
      color: white;
      background-color: $gray-900;
      font-weight: normal !important;
      &:focus {
        border: none !important;
        box-shadow: none !important;
      }
    }
  }
  .glyphicon {
    font-size: 0.625rem;
  }
}

.datepicker-main {
  padding: 0;

  .days {
    .days-of-week {
      text-transform: uppercase;
      background: $gray-800;
      color: white;
      .dow {
        font-weight: 600;
        height: 1.25rem;
        line-height: 1.25rem;
        font-size: 0.625rem;
      }
    }
    .datepicker-grid {
      padding: 0 5px 5px 5px;
      .datepicker-cell {
        height: 2.475em;
        line-height: 2.475em;
      }
    }
  }
  .datepicker-grid {
    margin-top: 5px;
    padding: 0 1.5em 1em;
    .datepicker-cell {
      height: 3.75em;
      line-height: 3.75em;
    }

    .datepicker-cell:hover {
      background-color: $indigo-200 !important;
      color: $gray-900;
    }

    .datepicker-cell.focused,
    .datepicker-cell.focused:hover,
    .datepicker-cell.selected,
    .datepicker-cell.selected:hover {
      background-color: $gray-900 !important;
      color: white;
    }
    span {
      border-radius: 50%;
    }
  }
}
</style>
